/**
 * Registry of the stage downloads in flight on this page, so the account purge
 * (`@/lib/media/mediaAccountBoundary`) can stop them and wait until they have stopped writing,
 * instead of each trigger keeping a private AbortController nobody else can reach.
 *
 * An entry stays registered until its run settles, not merely until it is aborted: abort makes
 * the pending fetches reject, but a download already past its fetch (a native file write, a Dexie
 * put) keeps going until it returns, and that tail is exactly what the purge must outwait.
 */

export type ActivePrefetch = {
  readonly signal: AbortSignal
  abort: () => void
  /** Settles with the run: resolves, or rejects with its error. */
  readonly done: Promise<void>
}

type Entry = { controller: AbortController; settled: Promise<void> }

const active = new Set<Entry>()

export function startPrefetch(run: (signal: AbortSignal) => Promise<void>): ActivePrefetch {
  const controller = new AbortController()
  // Called synchronously, as before; a synchronous throw becomes a rejection of `done`.
  const done = (async () => run(controller.signal))()
  const entry: Entry = {
    controller,
    settled: done.then(
      () => undefined,
      () => undefined,
    ),
  }
  active.add(entry)
  void entry.settled.then(() => active.delete(entry))
  return { signal: controller.signal, abort: () => controller.abort(), done }
}

/**
 * Aborts every registered download and waits for all of them to settle, for at most
 * `timeoutMs`. Never rejects. Resolves `true` when everything settled in time, `false` when the
 * wait ran out — the caller then goes on regardless, and the write generation
 * (`invalidateAttachmentWrites`) is what keeps a straggler from committing.
 */
export async function abortActivePrefetches(timeoutMs: number): Promise<boolean> {
  const entries = [...active]
  if (entries.length === 0) return true
  for (const entry of entries) entry.controller.abort()

  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<false>(resolve => {
    timer = setTimeout(() => resolve(false), timeoutMs)
  })
  const allSettled = Promise.all(entries.map(entry => entry.settled)).then(() => true as const)
  try {
    return await Promise.race([allSettled, timedOut])
  } finally {
    clearTimeout(timer)
  }
}

/** Number of registered downloads that have not settled yet (diagnostics and tests). */
export function activePrefetchCount(): number {
  return active.size
}
